/**
 * Login screen — two flows on one URL:
 *
 *   1. Office (default)   — email + password → POST /api/auth/login
 *   2. Worker (PWA-floor) — tab_number + 4-digit PIN → POST /api/field/auth/login
 *
 * The split is a local `mode` state machine: office | worker-tab. Worker
 * mode renders a NumericPad for the employee number (3-6 digits) and then
 * opens `<PinSheet>` to collect the PIN — same component the signature
 * screen will reuse (§3.6), so the worker meets the identical paddle
 * three times: tab entry, PIN entry, hours entry.
 *
 * Why not a separate /login/worker route?
 *   - One installable PWA, one URL surface. Backend doesn't care which
 *     mode produced the credentials (separate endpoints, same session
 *     cookie + CSRF).
 *   - If the office user accidentally taps the worker CTA (wall-tablet),
 *     `← Обычный вход` bounces them back without losing typed email.
 *
 * Error mapping (worker flow):
 *   - 401 invalid_credentials → PinSheet error body (counter), pad clears
 *   - 403 account_disabled    → toast, sheet closes (terminal)
 *   - 423 account_locked      → PinSheet lockout body, single CTA close
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Hash } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { login, type LoginRequest } from "@/api/auth";
import { workerLogin, type WorkerLoginRequest } from "@/api/worker-auth";
import { useInvalidateMe } from "@/hooks/useMe";
import { NumericPad } from "@/components/field/NumericPad";
import { PinSheet, type PinSheetState } from "@/components/field/PinSheet";
import { BrandLockup } from "@/components/brand/BrandLockup";
import { cn } from "@/lib/utils";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import { EMAIL_VALIDATION_ERROR, emailSchema } from "@/lib/emailValidation";
import {
  apiErrorFormIssue,
  FormErrorSummary,
  FormFieldError,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";
import { presentApiError } from "@/lib/api-error-policy";

const schema = z.object({
  email: emailSchema,
  password: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

type Mode = "office" | "worker-tab";

function hasDetailCode(detail: unknown, code: string): boolean {
  return (
    typeof detail === "object" &&
    detail !== null &&
    "code" in detail &&
    (detail as { code?: unknown }).code === code
  );
}

export interface LoginPageProps {
  /** Called after a successful login + /me invalidation. Router passes a navigate fn. */
  onSuccess?: () => void;
}

export function LoginPage({ onSuccess }: LoginPageProps): React.ReactElement {
  const [mode, setMode] = React.useState<Mode>("office");

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      {mode === "office" ? (
        <OfficeLogin onSuccess={onSuccess} onSwitchToWorker={() => setMode("worker-tab")} />
      ) : (
        <WorkerLogin onSuccess={onSuccess} onBack={() => setMode("office")} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Office (email + password)
// ─────────────────────────────────────────────────────────────────────────────

interface OfficeLoginProps {
  onSuccess?: () => void;
  onSwitchToWorker: () => void;
}

function OfficeLogin({ onSuccess, onSwitchToWorker }: OfficeLoginProps): React.ReactElement {
  const { t } = useTranslation();
  const invalidateMe = useInvalidateMe();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });
  useReleaseDirtyState(isDirty);

  const mutation = useMutation({
    mutationFn: (payload: LoginRequest) => login(payload),
    onSuccess: () => {
      invalidateMe();
      onSuccess?.();
    },
  });

  const serverIssue = mutation.error
    ? apiErrorFormIssue(mutation.error, {
        id: "login-server-error",
        fallbackMessage: t("common.apiError.fallback"),
        domainRules: [
          {
            code: "invalid_credentials",
            status: 401,
            detail: "invalid credentials",
            message: t("login.invalidCredentials"),
          },
        ],
      })
    : null;

  const issues: FormValidationIssue[] = [
    ...(errors.email
      ? [
          {
            id: "login-email-error",
            message:
              errors.email.message === EMAIL_VALIDATION_ERROR.reservedDomain
                ? t("validation.emailReservedDomain")
                : t("validation.emailInvalid"),
            kind: "field" as const,
            controlId: "email",
          },
        ]
      : []),
    ...(errors.password
      ? [
          {
            id: "login-password-error",
            message: t("login.passwordRequired"),
            kind: "field" as const,
            controlId: "password",
          },
        ]
      : []),
    ...(serverIssue ? [serverIssue] : []),
  ];

  const submit = handleSubmit(
    (values) => mutation.mutate(values),
    (invalidErrors) => {
      const first = invalidErrors.email ? "email" : "password";
      requestAnimationFrame(() => focusValidationControl(first));
    },
  );

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-7">
      <BrandLockup size={34} />
      <Card className="w-full ring-0">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">{t("login.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit} noValidate>
            <div className="space-y-1">
              <label htmlFor="email" className="text-base font-medium">
                {t("login.email")}
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "login-email-error" : undefined}
                className="h-11 text-lg"
                {...register("email")}
              />
              <FormFieldError issue={issues.find((issue) => issue.id === "login-email-error")} />
            </div>
            <div className="space-y-1">
              <label htmlFor="password" className="text-base font-medium">
                {t("login.password")}
              </label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? "login-password-error" : undefined}
                className="h-11 text-lg"
                {...register("password")}
              />
              <FormFieldError issue={issues.find((issue) => issue.id === "login-password-error")} />
            </div>
            <FormErrorSummary
              issues={issues}
              countLabel={(count) => t("validation.fieldErrorCount", { count })}
              data-testid="login-error-summary"
            />
            <Button
              type="submit"
              className="h-11 w-full text-base font-semibold tracking-[0.08em] uppercase"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>

          {/* «or» divider — keeps the office form visually self-contained,
            then introduces the worker CTA as a separate concern. */}
          <div className="my-5 flex items-center gap-3" aria-hidden>
            <div className="h-px flex-1" style={{ background: "var(--rule-line)" }} />
            <span
              className="font-condensed text-xs tracking-[0.18em] uppercase"
              style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
            >
              {t("login.workerMode.divider")}
            </span>
            <div className="h-px flex-1" style={{ background: "var(--rule-line)" }} />
          </div>

          {/* Worker CTA — plate-styled, industrial accent, hard-offset shadow.
            Big enough that a worker walking up to the wall tablet hits it
            without thinking about email/password. */}
          <button
            type="button"
            onClick={onSwitchToWorker}
            data-testid="login-switch-worker"
            className={cn(
              "group relative flex w-full items-center justify-center gap-3",
              "py-4 select-none",
              "transition-[background-color,box-shadow,transform] duration-75",
              "active:translate-y-[1px]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            )}
            style={{
              background: "var(--bg-panel-0)",
              color: "var(--fg-1)",
              border: "1px solid var(--rule-strong)",
              borderRadius: "var(--radius-2, 4px)",
              boxShadow: "var(--shadow-plate)",
              ["--tw-ring-color" as string]: "var(--accent-agent)",
            }}
          >
            <span
              aria-hidden
              className="flex h-7 w-7 items-center justify-center"
              style={{
                background: "var(--accent-agent)",
                color: "#fff",
                borderRadius: "var(--radius-1, 2px)",
              }}
            >
              <Hash className="h-4 w-4" strokeWidth={2.5} aria-hidden />
            </span>
            <span
              className="font-condensed text-sm font-semibold tracking-[0.12em] uppercase"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t("login.workerMode.switchTo")}
            </span>
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Worker (tab_number + PIN)
// ─────────────────────────────────────────────────────────────────────────────

interface WorkerLoginProps {
  onSuccess?: () => void;
  onBack: () => void;
}

const TAB_MIN = 3;
const TAB_MAX = 6;

function WorkerLogin({ onSuccess, onBack }: WorkerLoginProps): React.ReactElement {
  const { t } = useTranslation();
  const invalidateMe = useInvalidateMe();

  const [tab, setTab] = React.useState("");
  const [pinOpen, setPinOpen] = React.useState(false);
  const [pinState, setPinState] = React.useState<PinSheetState>({ kind: "idle" });
  // We keep a local attempt counter purely for UI — backend lockout policy
  // is authoritative (returns 423 with `locked_until`); we never gate
  // submission on the local counter.
  const [attempts, setAttempts] = React.useState(0);
  useReleaseDirtyState(tab !== "");

  const mutation = useMutation({
    mutationFn: (payload: WorkerLoginRequest) => workerLogin(payload),
    onMutate: () => {
      setPinState({ kind: "submitting" });
    },
    onSuccess: () => {
      setPinState({ kind: "success" });
      invalidateMe();
      // Defer close + navigate one tick so the success state is observable
      // (and so we don't fire navigate during the React render commit).
      setPinOpen(false);
      onSuccess?.();
    },
    onError: (error) => {
      const presentation = presentApiError(error, {
        fallbackMessage: t("common.apiError.fallback"),
        domainRules: [
          {
            code: "invalid_credentials",
            status: 401,
            detail: "invalid_credentials",
            message: t("field.pin.error.attempt", { attempt: attempts + 1, max: 3 }),
          },
          {
            code: "account_disabled",
            status: 403,
            detail: "account_disabled",
            message: t("field.login.errors.accountDisabled"),
          },
          {
            code: "account_locked",
            status: 423,
            matches: (detail) => hasDetailCode(detail, "account_locked"),
            message: t("field.pin.lockout.message"),
          },
        ],
      });
      if (presentation.kind === "domain") {
        if (presentation.code === "invalid_credentials") {
          const next = attempts + 1;
          setAttempts(next);
          setPinState({ kind: "error", attempts: next, maxAttempts: 3 });
          return;
        }
        if (presentation.code === "account_disabled") {
          toast.error(presentation.message);
          setPinOpen(false);
          setPinState({ kind: "idle" });
          return;
        }
        if (presentation.code === "account_locked") {
          setPinState({ kind: "lockout" });
          return;
        }
      }
      const next = attempts + 1;
      setAttempts(next);
      setPinState({
        kind: "error",
        attempts: next,
        maxAttempts: 3,
        message:
          presentation.kind === "fallback" ? presentation.message : t("common.apiError.fallback"),
        requestId: presentation.requestId,
      });
    },
  });

  const submitPin = (pin: string): void => {
    mutation.mutate({ tab_number: tab, pin });
  };

  const handlePinSheetOpenChange = (next: boolean): void => {
    if (!next) {
      // Sheet closing — reset transient state but keep the tab_number so the
      // user can retry without retyping (assessment: «не reload»).
      setPinOpen(false);
      setPinState({ kind: "idle" });
      setAttempts(0);
      mutation.reset();
    } else {
      setPinOpen(true);
    }
  };

  const canSubmitTab = tab.length >= TAB_MIN;

  return (
    <Card
      className="w-full max-w-sm overflow-hidden ring-0"
      data-testid="worker-login-card"
      style={{
        background: "var(--bg-panel-0)",
      }}
    >
      <CardHeader className="space-y-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="worker-login-back"
          className={cn(
            "inline-flex items-center gap-1.5 self-start text-xs tracking-[0.08em] uppercase",
            "font-condensed transition-colors",
            "hover:text-[color:var(--fg-1)] focus-visible:outline-none",
            "focus-visible:underline focus-visible:underline-offset-4",
          )}
          style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
          {t("field.login.backToOffice")}
        </button>
        <CardTitle
          className="font-condensed text-xl font-semibold tracking-[0.10em] uppercase"
          style={{ color: "var(--fg-1)", fontFamily: "var(--font-display)" }}
        >
          {t("field.login.title")}
        </CardTitle>
        <p
          className="font-condensed text-xs tracking-[0.06em] uppercase"
          style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
        >
          {t("field.login.subtitle")}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-5 pb-6">
        <TabDigits
          value={tab}
          max={TAB_MAX}
          aria-label={t("field.login.digitsAria", { filled: tab.length })}
        />

        <NumericPad
          length={{ min: TAB_MIN, max: TAB_MAX }}
          value={tab}
          onChange={setTab}
          onSubmit={() => {
            if (!canSubmitTab) return;
            setPinState({ kind: "idle" });
            setAttempts(0);
            setPinOpen(true);
          }}
          aria-label={t("field.login.title")}
          data-testid="worker-login-tab-pad"
        />
      </CardContent>

      <PinSheet
        open={pinOpen}
        onOpenChange={handlePinSheetOpenChange}
        title={t("field.pin.title")}
        description={t("field.pin.context.login")}
        state={pinState}
        onSubmit={submitPin}
        data-testid="worker-login-pin-sheet"
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TabDigits — small digit-box row mirroring PinDots, sized for 3-6 chars.
// ─────────────────────────────────────────────────────────────────────────────

interface TabDigitsProps {
  value: string;
  max: number;
  "aria-label": string;
}

function TabDigits({ value, max, "aria-label": ariaLabel }: TabDigitsProps): React.ReactElement {
  // Ширину контейнера привязываем к ширине грида NumericPad
  // (3 плитки + 2 gap), чтобы digit-cells и pad визуально были flush
  // (§20/6.1, photo_11). Reuse тех же CSS-vars `--tile-w` / `--tile-gap`
  // через класс `numeric-pad` — единый размер плиток 64w на всех viewport'ах
  // (§20/16.1). Высота input-cells — Tailwind `h-12` (48px), независима от
  // токенов NumericPad.
  return (
    <div
      role="status"
      aria-label={ariaLabel}
      data-testid="worker-login-tab-digits"
      className="numeric-pad flex items-center"
      style={{
        width: "calc(3 * var(--tile-w) + 2 * var(--tile-gap))",
        gap: "var(--tile-gap)",
      }}
    >
      {Array.from({ length: max }, (_, i) => {
        const ch = value[i];
        const isFilled = ch !== undefined;
        return (
          <span
            key={i}
            data-filled={isFilled || undefined}
            className="flex h-12 flex-1 items-center justify-center font-mono text-lg"
            style={{
              minWidth: 0,
              borderRadius: "var(--radius-1, 2px)",
              border: "1px solid var(--rule-strong)",
              background: isFilled ? "var(--bg-panel-1)" : "transparent",
              color: "var(--fg-1)",
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
              boxShadow: isFilled ? "var(--shadow-plate)" : undefined,
            }}
          >
            {ch ?? ""}
          </span>
        );
      })}
    </div>
  );
}
